import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["brandKitId"];
import { getHttpClientAsLastParam } from 'ContentData/helpers/apiHelpers';
import Categories from 'ContentUtils/constants/Categories';
import { isNullOrUndefined } from 'ContentUtils/helpers/ObjectHelpers';
import { DEFAULT_BUID } from '../constants/apiDefaults';
import { createRpcClientV2 } from 'rpc-client-utils';
import http from 'hub-http/clients/apiClient';
import { isPortalUngatedToMigrationRpc } from '../../__generated__/chirp/com/hubspot/branding/rpc/BrandAndSiteSettingsDiffRpc';
import { listByPortalIdRpc as listColorsByPortalIdRpc, listByBrandKitIdRpc as listColorsByBrandKitIdRpc } from '../../__generated__/chirp/com/hubspot/branding/rpc/BrandingColorRpc';
import { getAllBrandingDataRpc } from '../../__generated__/chirp/com/hubspot/branding/rpc/BrandingDataRpc';
import { getByBusinessUnitRpc as getKitByBusinessUnitRpc, getPrimaryRpc as getPrimaryKitRpc, createRpc as createKitRpc } from '../../__generated__/chirp/com/hubspot/branding/rpc/BrandKitRpc';
import { getByDomainOrFallbackToPrimaryRpc, createRpc as createLogoRpc } from '../../__generated__/chirp/com/hubspot/branding/rpc/BrandingLogoRpc';
function getRpcClient(_rest) {
  return createRpcClientV2({
    hubHttpClient: getHttpClientAsLastParam(_rest)
  });
}
export async function fetchBrandColors(businessUnitId, ..._rest) {
  const rpcClient = getRpcClient(_rest);
  if (Number.isInteger(businessUnitId)) {
    const kit = await rpcClient.call(getKitByBusinessUnitRpc, {
      request: {
        businessUnitId: businessUnitId
      }
    });
    const response = await rpcClient.call(listColorsByBrandKitIdRpc, {
      request: {
        brandKitId: kit.id
      }
    });
    return response.colors;
  }
  const response = await rpcClient.call(listColorsByPortalIdRpc, {});
  return response.colors;
}
export async function fetchBranding(..._rest) {
  var _response$branding;
  const rpcClient = getRpcClient(_rest);
  const response = await rpcClient.call(getAllBrandingDataRpc, {});
  return {
    branding: (_response$branding = response.branding) !== null && _response$branding !== void 0 ? _response$branding : {
      primaryColor: '',
      secondaryColor: ''
    },
    brandKitData: response.brandKitData.map(kit => {
      var _kit$accentColor, _kit$brandKitFontMeta, _kit$brandKitFontMeta2, _kit$brandKitFontMeta3;
      const colorsByType = new Map(kit.colors.map(c => [c.colorType, c]));
      return {
        brandKit: kit.brandKit,
        colors: kit.colors,
        logos: kit.logos,
        fonts: kit.fonts,
        primaryColor: colorsByType.get('PRIMARY'),
        secondaryColor: colorsByType.get('SECONDARY'),
        accentColor1: colorsByType.get('ACCENT_1'),
        accentColor2: colorsByType.get('ACCENT_2'),
        accentColor3: (_kit$accentColor = kit.accentColor3) !== null && _kit$accentColor !== void 0 ? _kit$accentColor : colorsByType.get('ACCENT_3'),
        primaryFontMeta: (_kit$brandKitFontMeta = kit.brandKitFontMeta) === null || _kit$brandKitFontMeta === void 0 ? void 0 : _kit$brandKitFontMeta.find(fm => fm.fontType === 'PRIMARY'),
        secondaryFontMeta: (_kit$brandKitFontMeta2 = kit.brandKitFontMeta) === null || _kit$brandKitFontMeta2 === void 0 ? void 0 : _kit$brandKitFontMeta2.find(fm => fm.fontType === 'SECONDARY'),
        fallbackFontMeta: (_kit$brandKitFontMeta3 = kit.brandKitFontMeta) === null || _kit$brandKitFontMeta3 === void 0 ? void 0 : _kit$brandKitFontMeta3.find(fm => fm.fontType === 'BRAND_KIT_FALLBACK')
      };
    })
  };
}
export function fetchBusinessUnit(contentType, contentId, ...rest) {
  const httpClient = getHttpClientAsLastParam(rest);
  const typeApiSlug = Categories[contentType] ? Categories[contentType].api_slug : contentType;
  const endpoint = contentType === Categories['blog_post'].name ? `blogs/v3/blog-posts/${contentId}` : `cospages/v2/${typeApiSlug}/${contentId}`;
  return httpClient.get(`${endpoint}?property=businessUnitId`);
}
export async function fetchBrandKit(businessUnitId, ..._rest) {
  const rpcClient = getRpcClient(_rest);
  const buId = isNullOrUndefined(businessUnitId) ? DEFAULT_BUID : businessUnitId;
  return await rpcClient.call(getKitByBusinessUnitRpc, {
    request: {
      businessUnitId: buId
    }
  });
}
export async function fetchLogo(domain, businessUnitId, ..._rest) {
  const rpcClient = getRpcClient(_rest);
  const response = await rpcClient.call(getByDomainOrFallbackToPrimaryRpc, {
    request: Object.assign({}, domain && {
      domain
    }, businessUnitId != null && {
      businessUnitId
    })
  });
  return response.logo;
}
export async function createLogo(logoData, query, ..._rest) {
  const rpcClient = getRpcClient(_rest);
  const {
      brandKitId
    } = logoData,
    logoRequest = _objectWithoutPropertiesLoose(logoData, _excluded);
  return await rpcClient.call(createLogoRpc, {
    request: Object.assign({
      brandKitId,
      logoRequest
    }, (query === null || query === void 0 ? void 0 : query.makePrimary) && {
      makePrimary: true
    })
  });
}
const rpcClient = createRpcClientV2({
  hubHttpClient: http
});
export function fetchIsPortalUngatedToMigration() {
  return rpcClient.call(isPortalUngatedToMigrationRpc, {});
}
export const fetchPrimaryBrandKit = async (createIfNotFound = false, ..._rest) => {
  const client = getRpcClient(_rest);
  try {
    return await client.call(getPrimaryKitRpc, {});
  } catch (e) {
    var _ref, _e$cause$cause$status, _e$cause, _e$cause2;
    const status = (_ref = (_e$cause$cause$status = e === null || e === void 0 || (_e$cause = e.cause) === null || _e$cause === void 0 || (_e$cause = _e$cause.cause) === null || _e$cause === void 0 ? void 0 : _e$cause.status) !== null && _e$cause$cause$status !== void 0 ? _e$cause$cause$status : e === null || e === void 0 || (_e$cause2 = e.cause) === null || _e$cause2 === void 0 ? void 0 : _e$cause2.status) !== null && _ref !== void 0 ? _ref : e === null || e === void 0 ? void 0 : e.status;
    if (status !== 404 || !createIfNotFound) {
      throw e;
    }
    return await client.call(createKitRpc, {
      request: {
        name: 'My brand kit'
      }
    });
  }
};